import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from "../components/ui/Table";
import { api } from "../lib/api";
import type { EndpointsHealth } from "../lib/types";

const REFRESH_MS = 30_000;

export function EndpointsPage() {
  const probe = useQuery<EndpointsHealth>({
    queryKey: ["admin-endpoints"],
    queryFn: () => api<EndpointsHealth>("/v1/admin/endpoints"),
    refetchInterval: REFRESH_MS,
  });

  const data = probe.data;
  const checkedAt = data ? new Date(data.checked_at) : null;

  return (
    <div className="flex flex-col gap-6">
      {/* Status strip + refresh. The sidebar already labels the page so we
          don't repeat the title here — keeps the page first-line
          informational, not chrome. */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-sm">
          {probe.isLoading && (
            <span className="text-gray-500 dark:text-gray-400">checking…</span>
          )}
          {probe.isError && <Badge variant="error">probe request failed</Badge>}
          {data && data.all_ok && <Badge variant="success">all healthy</Badge>}
          {data && !data.all_ok && (
            <Badge variant="error">degraded — see rows below</Badge>
          )}
          {checkedAt && (
            <span className="text-xs text-gray-500 dark:text-gray-500">
              checked {checkedAt.toLocaleTimeString()}
            </span>
          )}
        </div>
        <Button
          variant="secondary"
          onClick={() => probe.refetch()}
          disabled={probe.isFetching}
        >
          <RefreshCw
            size={14}
            className={probe.isFetching ? "animate-spin" : ""}
          />
          Refresh
        </Button>
      </div>

      <TableRoot>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>health</TableHeaderCell>
              <TableHeaderCell>method</TableHeaderCell>
              <TableHeaderCell>path</TableHeaderCell>
              <TableHeaderCell>status</TableHeaderCell>
              <TableHeaderCell>latency</TableHeaderCell>
              <TableHeaderCell>note</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.probes.map((p) => (
              <TableRow key={`${p.method} ${p.path}`}>
                <TableCell>
                  {p.ok ? (
                    <Badge variant="success">ok</Badge>
                  ) : (
                    <Badge variant="error">fail</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <code className="text-xs text-gray-600 dark:text-gray-400">
                    {p.method}
                  </code>
                </TableCell>
                <TableCell>
                  <code className="text-xs">{p.path}</code>
                </TableCell>
                <TableCell>
                  <span
                    className={
                      p.ok
                        ? "text-gray-700 dark:text-gray-300"
                        : "text-red-600 dark:text-red-400"
                    }
                  >
                    {p.status === 0 ? "—" : p.status}
                  </span>
                  <span className="ml-1 text-xs text-gray-400 dark:text-gray-600">
                    (expected {p.expected.join(" or ")})
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    className={
                      p.latency_ms > 1000
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-gray-600 dark:text-gray-400"
                    }
                  >
                    {p.latency_ms} ms
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {p.note ?? ""}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableRoot>
    </div>
  );
}
